import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Badge, Button, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import { usePageTitle } from '../context/PageTitleContext';
import { useAuth } from '../context/AuthContext';
import { generateSlug } from '../utils/slugUtils';
import RestaurantCard from './RestaurantCard';
import { useSavedRestaurantIds } from '../hooks/useSavedRestaurantIds';
import { renderRichText } from '../utils/richText';
import { normalizeCrop, croppedImageStyle } from './CroppedImage';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const CuisineDetail = () => {
  const { cuisineId } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const { token } = useAuth();
  const [cuisine, setCuisine] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('about');
  // #67: shared card needs the saved-ids Sets to show ❤️/📌 badges. Called once
  // here (the hook fires 2 API calls) and passed down, not per-card.
  const { favoriteIds, wishlistIds } = useSavedRestaurantIds();

  useEffect(() => {
    const fetchCuisineData = async () => {
      try {
        setLoading(true);

        // Fetch cuisine details
        const cuisineResponse = await axios.get(`${API_BASE_URL}/cuisines/${cuisineId}`);
        setCuisine(cuisineResponse.data);
        setPageTitle(cuisineResponse.data.name); // Set page title for navbar

        // Fetch restaurants for this cuisine. Owner-aware since #177: send the
        // token so your own private and awaiting-approval places appear (#393).
        const restaurantsResponse = await axios.get(`${API_BASE_URL}/cuisines/${cuisineId}/restaurants`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setRestaurants(restaurantsResponse.data);

        // Fetch foods for this cuisine
        const foodsResponse = await axios.get(`${API_BASE_URL}/cuisines/${cuisineId}/foods`);
        setFoods(foodsResponse.data);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching cuisine data:', err);
        setError('Failed to load cuisine information');
        setLoading(false);
      }
    };

    fetchCuisineData();
    // `token` is a dependency so signing out refetches and drops your private rows.
  }, [cuisineId, token]);

  // Accept either a real array or the legacy JSON-encoded string (#178).
  //
  // The API now returns these as arrays — it had to, because the iOS model
  // declares [String]? and a string made every enriched cuisine fail to
  // decode. JSON.parse(anArray) would stringify it first and throw, silently
  // emptying the section here, so this checks for an array before parsing.
  const parseList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!cuisine) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">Cuisine not found</Alert>
      </Container>
    );
  }

  const typicalIngredients = parseList(cuisine.typical_ingredients);
  const popularDishes = parseList(cuisine.popular_dishes);
  const hasEnrichedData = cuisine.last_enriched !== null;

  return (
    <Container className="mt-4 mb-5">
      {/* Header */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            {cuisine.description && (
              <p className="lead" style={{ color: '#9FD9C7' }}>{renderRichText(cuisine.description)}</p>
            )}
          </div>
          {hasEnrichedData && (
            <Badge bg="success" className="ms-2">
              Enhanced Info Available
            </Badge>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="mb-4 border-bottom">
        <Button
          variant="link"
          className={`me-3 ${activeTab === 'about' ? 'fw-bold text-primary' : 'text-muted'}`}
          onClick={() => setActiveTab('about')}
          style={{ textDecoration: 'none' }}
        >
          About
        </Button>
        <Button
          variant="link"
          className={`me-3 ${activeTab === 'restaurants' ? 'fw-bold text-primary' : 'text-muted'}`}
          onClick={() => setActiveTab('restaurants')}
          style={{ textDecoration: 'none' }}
        >
          Restaurants ({restaurants.length})
        </Button>
        <Button
          variant="link"
          className={activeTab === 'foods' ? 'fw-bold text-primary' : 'text-muted'}
          onClick={() => setActiveTab('foods')}
          style={{ textDecoration: 'none' }}
        >
          Foods ({foods.length})
        </Button>
      </div>

      {/* Content */}
      {activeTab === 'about' && (
        <div className="p-4 rounded" style={{ backgroundColor: '#f0f8f7' }}>
          {!hasEnrichedData && (
            <Alert variant="light" className="border">
              <small className="text-muted">
                <strong>ℹ️ Coming soon:</strong> We're working on adding detailed information about popular dishes, typical ingredients, and cultural context for this cuisine.
              </small>
            </Alert>
          )}

          {hasEnrichedData && (
            <Row>
              {/* Popular Dishes */}
              {popularDishes.length > 0 && (
                <Col md={6} className="mb-4">
                  <Card>
                    <Card.Header className="bg-primary text-white">
                      <h5 className="mb-0">Popular Dishes</h5>
                    </Card.Header>
                    <Card.Body>
                      <ul className="list-unstyled mb-0">
                        {popularDishes.map((dish, idx) => (
                          <li key={idx} className="mb-2">
                            <span className="text-primary me-2">🍽️</span>
                            {dish}
                          </li>
                        ))}
                      </ul>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {/* Typical Ingredients */}
              {typicalIngredients.length > 0 && (
                <Col md={6} className="mb-4">
                  <Card>
                    <Card.Header className="bg-success text-white">
                      <h5 className="mb-0">Typical Ingredients</h5>
                    </Card.Header>
                    <Card.Body>
                      <div className="d-flex flex-wrap gap-2">
                        {typicalIngredients.slice(0, 15).map((ingredient, idx) => (
                          <Badge
                            key={idx}
                            bg="light"
                            text="dark"
                            className="px-3 py-2"
                            style={{ fontSize: '0.9rem' }}
                          >
                            {ingredient}
                          </Badge>
                        ))}
                        {typicalIngredients.length > 15 && (
                          <Badge
                            bg="secondary"
                            className="px-3 py-2"
                            style={{ fontSize: '0.9rem' }}
                          >
                            +{typicalIngredients.length - 15} more
                          </Badge>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {/* Flavor Profile */}
              {cuisine.flavor_profile && (
                <Col md={12} className="mb-4">
                  <Card>
                    <Card.Header className="bg-warning text-dark">
                      <h5 className="mb-0">Flavor Profile</h5>
                    </Card.Header>
                    <Card.Body>
                      <p className="mb-0">{cuisine.flavor_profile}</p>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {/* Additional Context (if available) */}
              {(cuisine.historical_context || cuisine.cultural_significance || cuisine.origin_country) && (
                <Col md={12} className="mb-4">
                  <Card>
                    <Card.Header className="bg-info text-white">
                      <h5 className="mb-0">Cultural Context</h5>
                    </Card.Header>
                    <Card.Body>
                      {cuisine.origin_country && (
                        <div className="mb-3">
                          <strong>Origin:</strong> {cuisine.origin_country}
                          {cuisine.origin_region && `, ${cuisine.origin_region}`}
                        </div>
                      )}
                      {cuisine.cultural_significance && (
                        <div className="mb-3">
                          <strong>Cultural Significance:</strong> {cuisine.cultural_significance}
                        </div>
                      )}
                      {cuisine.historical_context && (
                        <div>
                          <strong>Historical Context:</strong> {cuisine.historical_context}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {/* Data Source */}
              {cuisine.external_source && (
                <Col md={12}>
                  <small className="text-muted">
                    Enhanced information provided by {cuisine.external_source}
                    {cuisine.last_enriched && ` • Last updated: ${new Date(cuisine.last_enriched).toLocaleDateString()}`}
                  </small>
                </Col>
              )}
            </Row>
          )}
        </div>
      )}

      {activeTab === 'restaurants' && (
        <div className="p-4 rounded" style={{ backgroundColor: '#f0f8f7' }}>
          {restaurants.length === 0 ? (
            <Alert variant="info">No restaurants found for this cuisine.</Alert>
          ) : (
            <div className="row">
              {/* #67: shared RestaurantCard — same badges (fav/wishlist/open-now)
                  and layout as the main list; replaces the hand-rolled copy. */}
              {restaurants.map((restaurant) => (
                <div key={restaurant.id} className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-3">
                  <RestaurantCard
                    restaurant={restaurant}
                    favoriteIds={favoriteIds}
                    wishlistIds={wishlistIds}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'foods' && (
        <div className="p-4 rounded" style={{ backgroundColor: '#f0f8f7' }}>
          {foods.length === 0 ? (
            <Alert variant="info">No foods found for this cuisine.</Alert>
          ) : (
            <div className="row">
              {foods.map((food) => {
                // Get images from food.images (populated by backend validator).
                // #234: keep the rows, not just their URLs — each may carry the
                // per-dish crop the bridge brought across from a visit photo.
                const allImages = food.images?.slice(0, 4) || [];

                return (
                  <div key={food.id} className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-3">
                    <Card
                      className="shadow-sm h-100"
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(`/food/${food.id}/${generateSlug(food.name)}`)}
                    >
                      <Card.Body className="p-3">
                        <Card.Title className="h6 mb-2" style={{
                          fontSize: "1rem",
                          fontWeight: "600",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}>
                          {food.name}
                        </Card.Title>

                        {/* Dietary badges */}
                        <div className="mb-2">
                          {food.is_vegetarian === 1 && (
                            <Badge bg="success" className="me-1" style={{ fontSize: "0.7rem" }}>🥬 Veg</Badge>
                          )}
                          {food.is_vegan === 1 && (
                            <Badge bg="success" className="me-1" style={{ fontSize: "0.7rem" }}>🌱 Vegan</Badge>
                          )}
                          {food.is_gluten_free === 1 && (
                            <Badge bg="info" className="me-1" style={{ fontSize: "0.7rem" }}>GF</Badge>
                          )}
                        </div>

                        {food.base_price && (
                          <div className="text-muted small mb-3">
                            ${Number(food.base_price).toFixed(2)}
                          </div>
                        )}

                        {/* Image thumbnails */}
                        <div className="d-flex gap-2 overflow-hidden" style={{ height: "60px" }}>
                          {allImages.length > 0 ? (
                            allImages.map((img, idx) => {
                              // #234: an <img> in a clipping frame rather than a
                              // background-image, so this tile uses the SAME crop
                              // arithmetic as every other surface. Cropping a
                              // background would need background-position/size
                              // instead — a second implementation of one idea.
                              const crop = normalizeCrop(img);
                              return (
                                <div
                                  key={idx}
                                  style={{
                                    width: "80px",
                                    minWidth: "80px",
                                    height: "60px",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                    position: "relative"
                                  }}
                                >
                                  <img
                                    src={img.flickr_url}
                                    alt=""
                                    style={crop
                                      ? croppedImageStyle(crop)
                                      : { width: "100%", height: "100%", objectFit: "cover" }}
                                  />
                                </div>
                              );
                            })
                          ) : (
                            [...Array(4)].map((_, idx) => (
                              <div
                                key={idx}
                                style={{
                                  width: "80px",
                                  minWidth: "80px",
                                  height: "60px",
                                  backgroundColor: "#f0f0f0",
                                  borderRadius: "4px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}
                              >
                                <span style={{ fontSize: "20px", color: "#ccc" }}>🍽️</span>
                              </div>
                            ))
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Container>
  );
};

export default CuisineDetail;
